import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

export const categoryStyles: Record<string, string> = {
  film: "bg-primary-100 text-primary-700",
  variety: "bg-accent-100 text-accent-800",
  music: "bg-secondary-100 text-secondary-800",
  influencer: "bg-primary-50 text-primary-700",
  emotion: "bg-accent-50 text-accent-800",
  business: "bg-secondary-50 text-secondary-700",
};

export function CategoryTag({ slug, name }: { slug: string; name: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-medium ${
        categoryStyles[slug] ?? "bg-background-200 text-foreground-700"
      }`}
    >
      {name}
    </span>
  );
}

function MetaRow({ article }: { article: Article }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500 md:text-xs">
      <span className="flex items-center gap-1">
        <span className="flex h-3.5 w-3.5 items-center justify-center">
          <i className="ri-time-line"></i>
        </span>
        {article.date}
      </span>
      <span className="flex items-center gap-1">
        <span className="flex h-3.5 w-3.5 items-center justify-center">
          <i className="ri-eye-line"></i>
        </span>
        {(article.views / 1000).toFixed(1)}k 阅读
      </span>
      <span className="flex items-center gap-1">
        <span className="flex h-3.5 w-3.5 items-center justify-center">
          <i className="ri-chat-3-line"></i>
        </span>
        {article.comments}
      </span>
    </div>
  );
}

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "hero" | "compact" | "text";
  index?: number;
}

export default function ArticleCard({ article, variant = "default", index }: ArticleCardProps) {
  const href = `/article/${article.id}`;

  if (variant === "hero") {
    return (
      <article className="group relative h-[340px] overflow-hidden rounded-lg md:h-[460px]">
        <img
          src={article.cover}
          alt={`${article.title} 相关配图`}
          title={`${article.title} - 黑料八卦深扒`}
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/55 to-foreground-950/10"></div>
        <div className="relative flex h-full flex-col justify-end p-5 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryTag slug={article.categorySlug} name={article.category} />
            <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-foreground-950">
              <i className="ri-fire-fill"></i>
              独家深扒
            </span>
          </div>
          <h3 className="mt-4 max-w-3xl font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
            <Link to={href} className="cursor-pointer transition-colors hover:text-accent-300">
              {article.title}
            </Link>
          </h3>
          <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">
            {article.excerpt}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-background-300 md:text-xs">
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-user-3-line"></i>
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-eye-line"></i>
              </span>
              {(article.views / 1000).toFixed(1)}k 阅读
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {article.date}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group flex gap-4 border-b border-background-200 pb-4 last:border-0 last:pb-0">
        <Link
          to={href}
          className="relative h-[72px] w-[104px] flex-none cursor-pointer overflow-hidden rounded-md"
        >
          <img
            src={article.cover}
            alt={`${article.title} 相关配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0">
          <h4 className="font-heading text-sm font-semibold leading-snug text-foreground-950 md:text-base">
            <Link to={href} className="cursor-pointer transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h4>
          <p className="mt-1.5 line-clamp-2 text-xs text-foreground-600">{article.excerpt}</p>
          <div className="mt-2">
            <MetaRow article={article} />
          </div>
        </div>
      </article>
    );
  }

  if (variant === "text") {
    return (
      <article className="group flex items-start gap-3 border-b border-background-200 py-3 last:border-0">
        {typeof index === "number" && (
          <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md bg-background-100 font-heading text-xs font-bold text-foreground-500">
            {index + 1}
          </span>
        )}
        <div className="min-w-0">
          <h4 className="font-heading text-sm font-medium leading-snug text-foreground-900">
            <Link to={href} className="cursor-pointer transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h4>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-foreground-500">
            <span>{article.category}</span>
            <span>{article.date}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <Link to={href} className="relative block h-[180px] cursor-pointer overflow-hidden md:h-[196px]">
        <img
          src={article.cover}
          alt={`${article.title} 相关配图`}
          title={`${article.title} - 黑料八卦爆料`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <CategoryTag slug={article.categorySlug} name={article.category} />
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
          <Link to={href} className="cursor-pointer transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2.5 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {article.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-4 border-t border-background-200 pt-3">
          <MetaRow article={article} />
        </div>
      </div>
    </article>
  );
}